import store from '../../../store/store';
import { asyncHandler } from '../async-handler';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import {
  PRESENTATIONS_API,
  PRESENTATION_API,
  PRESENTATION_FREQUENCIES_API,
  PRESENTATIONS_ALL_SHORTCODES_API,
} from '../../constants/central-api';
import {
  Presentation,
  PresentationCreateRequest,
  PresentationUpdateRequest,
  PresentationResponse,
  PresentationFrequenciesResponse,
  PresentationShortcodesResponse,
  ShortcodeItem,
} from '../../../interfaces/Presentation';

// Get all presentations for a shop
export default async function get_presentations_api_call(): Promise<
  Presentation[] | null
> {
  try {
    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: PRESENTATIONS_API(shopSlug),
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'GET_PRESENTATIONS';
    const customMsg = 'Presentations retrieved successfully';

    const res: any = await asyncHandler(
      requestInfo,
      actionType,
      customMsg,
      false
    );

    const response: PresentationResponse = res?.data;

    if (res?.actionType === actionType && response?.success === true) {
      return response.data as Presentation[];
    } else {
      return null;
    }
  } catch (exception) {
    console.log('Error during get presentations request');
    return null;
  }
}

// Create a new presentation
export async function create_presentation_api_call(
  presentationData: PresentationCreateRequest
): Promise<Presentation | null> {
  try {
    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: PRESENTATIONS_API(shopSlug),
      method: 'POST',
      body: JSON.stringify(presentationData),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'CREATE_PRESENTATION';
    const customMsg = 'Presentation created successfully';

    const res: any = await asyncHandler(requestInfo, actionType, customMsg);

    const response: PresentationResponse = res?.data;

    if (res?.actionType === actionType && response?.success === true) {
      return response.data as Presentation;
    } else {
      return null;
    }
  } catch (exception) {
    console.log('Error during create presentation request');
    return null;
  }
}

// Update an existing presentation
export async function update_presentation_api_call(
  presentationId: string,
  presentationData: PresentationUpdateRequest
): Promise<Presentation | null> {
  try {
    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: PRESENTATION_API(shopSlug, presentationId),
      method: 'PUT',
      body: JSON.stringify(presentationData),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'UPDATE_PRESENTATION';
    const customMsg = 'Presentation updated successfully';

    const res: any = await asyncHandler(requestInfo, actionType, customMsg);

    const response: PresentationResponse = res?.data;

    if (res?.actionType === actionType && response?.success === true) {
      return response.data as Presentation;
    } else {
      return null;
    }
  } catch (exception) {
    console.log('Error during update presentation request');
    return null;
  }
}

// Delete a presentation
export async function delete_presentation_api_call(
  presentationId: string
): Promise<boolean> {
  try {
    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: PRESENTATION_API(shopSlug, presentationId),
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'DELETE_PRESENTATION';
    const customMsg = 'Presentation deleted successfully';

    const res: any = await asyncHandler(requestInfo, actionType, customMsg);

    const response: PresentationResponse = res?.data;

    if (res?.actionType === actionType && response?.success === true) {
      return true;
    } else {
      return false;
    }
  } catch (exception) {
    console.log('Error during delete presentation request');
    return false;
  }
}

// Get presentation frequencies
export async function get_presentation_frequencies_api_call(): Promise<{
  [key: string]: string;
} | null> {
  try {
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: PRESENTATION_FREQUENCIES_API,
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'GET_PRESENTATION_FREQUENCIES';
    const customMsg = 'Presentation frequencies retrieved successfully';

    const res: any = await asyncHandler(
      requestInfo,
      actionType,
      customMsg,
      false
    );

    const response: PresentationFrequenciesResponse = res?.data;

    if (res?.actionType === actionType && response?.success === true) {
      return response.data;
    } else {
      return null;
    }
  } catch (exception) {
    console.log('Error during get presentation frequencies request');
    return null;
  }
}

// Get all shortcodes for presentations
export async function get_presentation_shortcodes_api_call(): Promise<
  ShortcodeItem[] | null
> {
  try {
    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: PRESENTATIONS_ALL_SHORTCODES_API(shopSlug),
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'GET_PRESENTATION_SHORTCODES';
    const customMsg = 'Shortcodes retrieved successfully';

    const res: any = await asyncHandler(
      requestInfo,
      actionType,
      customMsg,
      false
    );

    const response: PresentationShortcodesResponse = res?.data;

    if (res?.actionType === actionType && response?.success === true) {
      const allShortcodes: ShortcodeItem[] = [];

      // Add insights
      if (response.data.insights) {
        response.data.insights.forEach((insight) => {
          allShortcodes.push({ ...insight, type: 'insight' });
        });
      }

      // Add custom_prompts
      if (response.data.custom_prompts) {
        response.data.custom_prompts.forEach((prompt) => {
          allShortcodes.push({ ...prompt, type: 'custom_prompt' });
        });
      }

      // Add custom_graphs
      if (response.data.custom_graphs) {
        response.data.custom_graphs.forEach((graph) => {
          allShortcodes.push({ ...graph, type: 'graph' });
        });
      }

      return allShortcodes.length > 0 ? allShortcodes : null;
    } else {
      return null;
    }
  } catch (exception) {
    console.log('Error during get presentation shortcodes request');
    return null;
  }
}
